import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import MarkdownRenderer from './MarkdownRenderer';

// Custom CopyButton component with inline hover, active styling, and "Copied" label.
const CopyButton = ({ onClick }) => {
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const buttonStyle = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    zIndex: 1,
    padding: '6px 12px',
    fontSize: '1rem',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderRadius: '4px',
    // When copied, force the color to yellow; otherwise, use hover/active logic.
    color: copied
      ? 'yellow'
      : active
      ? 'lightyellow'
      : hover
      ? 'yellow'
      : 'inherit'
  };

  const handleClick = () => {
    onClick();
    setCopied(true);
    // Revert back to "Copy" after 5 seconds.
    setTimeout(() => setCopied(false), 5000);
  };

  return (
    <button
      onClick={handleClick}
      style={buttonStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const MixedCodeResponse = ({ response }) => {
  // This regex finds code blocks with optional language, matching everything until the closing backticks.
  const codeRegex = /```(\w+)?\n([\s\S]*?)```/g;

  // Function to copy code to clipboard.
  const copyCode = (code) => {
    navigator.clipboard.writeText(code.trim())
      .then(() => {
        console.log("Code copied to clipboard!");
      })
      .catch((err) => {
        console.error("Error copying code:", err);
      });
  };

  // Process the response: split between code and markdown parts.
  const renderResponse = () => {
    let lastIndex = 0;
    const parts = [];

    response.replace(codeRegex, (match, language, code, offset) => {
      // Render markdown before each code block using our custom MarkdownRenderer.
      if (offset > lastIndex) {
        parts.push(
          <MarkdownRenderer key={lastIndex} content={response.slice(lastIndex, offset)} />
        );
      }

      // Render the code block with the custom CopyButton.
      parts.push(
        <div key={offset} style={{ position: 'relative', marginBottom: '1em' }}>
          <CopyButton onClick={() => copyCode(code)} />
          <SyntaxHighlighter
            language={language || 'text'}
            style={vscDarkPlus}
            showLineNumbers
          >
            {code.trim()}
          </SyntaxHighlighter>
        </div>
      );

      lastIndex = offset + match.length;
      return match;
    });

    // Render any remaining markdown after the last code block.
    if (lastIndex < response.length) {
      parts.push(
        <MarkdownRenderer key={lastIndex} content={response.slice(lastIndex)} />
      );
    }
    return parts;
  };

  return <div className="mixed-response">{renderResponse()}</div>;
};

export default MixedCodeResponse;
